import React, { useEffect, useState } from "react";
import ProfileFirst from "../../components/Profile/ProfileFirst";
import ProfileSecond from "../../components/Profile/ProfileSecond";
import ProfileThird from "../../components/Profile/ProfileThird";
import ProfileFourth from "../../components/Profile/Profilefourth";
import { useParams } from "react-router-dom";
import { getRequest } from "../../api/apiServices";
import { getCompany } from "../../api/apiList";
import Loading from "../../utils/Loading";
import { Box, Typography, useMediaQuery } from "@mui/material";
import OverviewIcon from "../../assets/images/JobIcon/Overview.svg";
import CheckIcon from "../../assets/images/JobIcon/check-bold.svg";
import TeamsIcon from "../../assets/images/JobIcon/team-people.svg";
import SymbolsIcon from "../../assets/images/JobIcon/symbols_step.svg";

const PROFILE_STEPS = [
  { page: 1, label: "Company\nDetails", icon: OverviewIcon },
  { page: 2, label: "Mission &\nIndustry", icon: CheckIcon },
  { page: 3, label: "Culture &\nBenefits", icon: TeamsIcon },
  { page: 4, label: "Social &\nIntegrations", icon: SymbolsIcon },
] as const;

const Profile: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [profileData, setProfileData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const isMobile = useMediaQuery("(max-width:900px)");

  const params = useParams();
  const isCreateMode = !params?.id;

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, 4));
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= 4) {
      setCurrentPage(page);
    }
  };

  const getCompanyData = async () => {
    const response = await getRequest(getCompany(params?.id));
    return response?.data?.data;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (params?.id) {
          setLoading(true);
          const data = await getCompanyData();
          setProfileData(data);
          setUpdate(true);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [params?.id]);

  const sharedProps = {
    setProfileData,
    initialData: profileData,
    mode: update,
    currentPage,
    onGoToPage: goToPage,
  };

  return loading ? (
    <Loading />
  ) : (
    <div
      className="create-company-profile"
      style={
        isCreateMode
          ? { backgroundColor: "#F0F0F0", minHeight: "100vh" }
          : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 0, md: 1.5 },
          px: { xs: 1, md: 2 },
          maxWidth: 1120,
          mx: "auto",
        }}
      >
        {!isMobile && (
          <Box sx={{ width: 168, flexShrink: 0, pt: "33px" }}>
            {PROFILE_STEPS.map((step, index) => (
              <Box
                key={step.page}
                mb={index < PROFILE_STEPS.length - 1 ? "20px" : 0}
              >
                <Box
                  className="box-shadow"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  alignItems="center"
                  bgcolor={currentPage === step.page ? "#F8FFFD" : "#FFFFFF"}
                  borderRadius="14px"
                  border={
                    currentPage === step.page
                      ? "1px solid #84E1CC"
                      : "1px solid #D9D9D9"
                  }
                  onClick={() => goToPage(step.page)}
                  sx={{
                    cursor: "pointer",
                    transition:
                      "border-color 0.2s ease, background-color 0.2s ease",
                    "&:hover": {
                      borderColor: "#84E1CC",
                    },
                  }}
                >
                  <Box pt="20px" px="16px">
                    <Box
                      p="12px"
                      borderRadius="7px"
                      bgcolor={
                        currentPage === step.page ? "#6fd4bc" : "#84E1CC"
                      }
                    >
                      <img src={step.icon} alt="" />
                    </Box>
                  </Box>
                  <Box px="12px" pb="18px" pt="12px" textAlign="center">
                    <Typography
                      fontSize="12px"
                      fontWeight={currentPage === step.page ? 600 : 500}
                      color="#273848"
                      whiteSpace="pre-line"
                      lineHeight="14px"
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiContainer-root": {
              marginLeft: isMobile ? "auto" : "0 !important",
              marginRight: isMobile ? "auto" : "0 !important",
              paddingLeft: { xs: "12px", md: "4px !important" },
              paddingRight: { xs: "12px", md: "16px" },
              justifyContent: isMobile
                ? "center !important"
                : "flex-start !important",
            },
          }}
        >
          {currentPage === 1 && (
            <ProfileFirst onNext={goToNextPage} {...sharedProps} />
          )}
          {currentPage === 2 && (
            <ProfileSecond
              onBack={goToPreviousPage}
              onNext={goToNextPage}
              {...sharedProps}
            />
          )}
          {currentPage === 3 && (
            <ProfileThird
              onBack={goToPreviousPage}
              onNext={goToNextPage}
              {...sharedProps}
            />
          )}
          {currentPage === 4 && (
            <ProfileFourth
              onBack={goToPreviousPage}
              onNext={goToNextPage}
              {...sharedProps}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default Profile;
